import MaterialIcon from '@components/common/MaterialIcon';
import { markdownToPreview } from '@components/designer/utils/markdownToPreview';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type {
  TArticle,
  TConversationDetailsSchema,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import { purify } from '@helpers/domHelper';
import { useModal } from '@hooks/modal/useModal';
import parse from 'html-react-parser';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getArticleSourceName = (article: TArticle): string => {
  return article.sourceName || '--';
};

const formatScore = (score?: number): string => {
  if (typeof score === 'undefined' || score === null) {
    return '--';
  }

  return `${(score * 100).toFixed(2)}%`;
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength)}...`;
};

type TArticleCardProps = {
  isTopArticle: boolean;
  article: TArticle;
  index: number;
};

// * Reasonable limit that still shows most of the content if truncated
const CONTENT_TRUNCATE_LENGTH = 256 * 5;

const ArticleCard: React.FC<TArticleCardProps> = ({
  isTopArticle,
  article,
  index,
}) => {
  const { t } = useTranslation();
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const { content = '--' } = article;

  // * Limit the content if it exceeds the truncate length threshold by using a "see more" button
  const shouldShowSeeMore = useMemo(
    () => content.length > CONTENT_TRUNCATE_LENGTH,
    [content],
  );
  const contentToDisplay = isContentExpanded
    ? content
    : truncateText(content, CONTENT_TRUNCATE_LENGTH);

  // Convert Markdown to HTML first, then sanitize and parse so that
  // formatting such as bold, italics, and links renders correctly
  const parsedContentToDisplay = useMemo(() => {
    return parse(
      purify(markdownToPreview(contentToDisplay), 'markdown-link-inherit'),
    );
  }, [contentToDisplay]);

  return (
    <div className='flex flex-col gap-2 p-3 bg-white rounded border border-gray-400'>
      <div className='flex justify-between items-start'>
        <span className='text-xs font-medium text-primary-base'>
          #{index + 1}
        </span>
        <span className='flex items-center gap-1 text-xs font-medium text-gray-600'>
          {isTopArticle && (
            <MaterialIcon
              icon='star'
              classes={['text-primary-base']}
              tooltip={t('train-ai.conversation-info.top-match')}
            />
          )}
          {t('train-ai.conversation-info.similarity')}:{' '}
          {formatScore(article.score)}
        </span>
      </div>
      <div className='flex flex-col gap-1'>
        <span className='text-xs font-medium text-gray-600'>
          {t('train-ai.conversation-info.source-file')}:
        </span>
        <span className='text-sm text-gray-700 break-all'>
          {getArticleSourceName(article)}
        </span>
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-xs font-medium text-gray-600'>
          {t('train-ai.conversation-info.article-content')}:
        </span>
        <div className='text-sm text-gray-700 formatted-html-content overflow-hidden break-words'>
          {parsedContentToDisplay}
          {shouldShowSeeMore && (
            <button
              type='button'
              onClick={() => setIsContentExpanded(!isContentExpanded)}
              className='ml-1 text-primary-base hover:underline font-medium text-sm'>
              {isContentExpanded
                ? t('general.buttons.show-less')
                : t('general.buttons.show-more')}
            </button>
          )}
        </div>
      </div>

      {article.url && (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-gray-600'>
            {t('train-ai.conversation-info.article-url')}:
          </span>
          <a
            href={article.url}
            target='_blank'
            rel='noreferrer'
            className='text-sm text-primary-base hover:underline break-all'>
            {article.url}
          </a>
        </div>
      )}
      {article.category && (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-gray-600'>
            {t('train-ai.conversation-info.article-category')}:
          </span>
          <span className='text-sm text-gray-600'>
            {article.category.split(',').pop()}
          </span>
        </div>
      )}
      {article.label && (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-gray-600'>
            {t('train-ai.conversation-info.article-label')}:
          </span>
          <span className='text-sm text-gray-600'>{article.label}</span>
        </div>
      )}
      {article.createdAt && (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-gray-600'>
            {t('train-ai.conversation-info.article-created-at')}:
          </span>
          <span className='text-sm text-gray-600'>
            {article?.createdAt
              ? new Date(article.createdAt).toLocaleString()
              : ''}
          </span>
        </div>
      )}
    </div>
  );
};

export const ConversationInfoModal: React.FC<TConversationDetailsSchema> = (
  conversation,
) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  const articles = useMemo(
    () => conversation.articles || [],
    [conversation.articles],
  );

  const hasArticles = useMemo(() => articles.length > 0, [articles]);

  const topArticleIndex = useMemo(() => {
    return articles.findIndex((article) => article.isTopArticle);
  }, [articles]);

  const topArticle = useMemo(() => {
    return topArticleIndex !== -1 ? articles[topArticleIndex] : null;
  }, [articles, topArticleIndex]);

  return (
    <form className='tailwind-modal-form flex-1 min-h-[300px] px-2 flex flex-col'>
      <div className='tailwind-modal-body flex flex-col h-inherit'>
        <div className='flex flex-col rounded-md border border-primary-base overflow-hidden'>
          <div className='flex flex-col bg-primary-dark text-white py-2 px-3 flex-shrink-0'>
            <span className='text-base font-normal'>
              {t('train-ai.conversation-info.subtitle')}
            </span>
            {topArticle && (
              <span className='text-sm font-medium mt-1'>
                {t('train-ai.conversation-info.top-match')}: #
                {topArticleIndex + 1} - {getArticleSourceName(topArticle)}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1 p-3 overflow-y-auto custom-scrollbar max-h-[60vh] bg-gray-100'>
            {hasArticles ? (
              <div className='flex flex-col gap-3 mb-3'>
                <h3 className='text-sm font-medium text-gray-600'>
                  {t('train-ai.conversation-info.vector-sources-title')} (
                  {articles.length})
                </h3>
                {articles.map((article, index) => (
                  <ArticleCard
                    isTopArticle={article.isTopArticle ?? false}
                    key={index}
                    article={article}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className='flex items-center justify-center py-8 px-4 bg-gray-200 rounded border border-gray-400'>
                <span className='text-sm text-gray-600 text-center'>
                  {t('train-ai.conversation-info.no-articles')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer px-0'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.close')}
        </ButtonT>
      </div>
    </form>
  );
};

export default ConversationInfoModal;
